// 
// 
// 


import React from 'react';
import { 
  FileSignature, ShieldCheck, SearchCheck, CheckCircle2, 
  Handshake, Building, Users2, ShieldAlert, FileText 
} from 'lucide-react';
import { ServicePageHeader } from '../ServicePageHeader';

export default function ContractServices() {
  
  // انواع العقود والاتفاقيات بتنسيق النمط المؤسسي الجديد (Navy Light)
  const contractTypes = [
    {
      title: "عقود الشركات والتأسيس",
      desc: "صياغة قرارات الشركاء، عقود التأسيس، والاتفاقيات بين المساهمين لضمان بيئة عمل مستقرة ومنظمة.",
      icon: <Building className="w-6 h-6 text-indigo-950" />
    },
    {
      title: "عقود العمل والتوظيف",
      desc: "إعداد لوائح تنظيم العمل الداخلية وعقود الموظفين بما يتوافق تماماً مع نظام العمل السعودي الجديد وحماية الطرفين.",
      icon: <Users2 className="w-6 h-6 text-indigo-950" />
    },
    {
      title: "العقود التجارية والخدمية",
      desc: "تشمل اتفاقيات التوريد، التوزيع، الوكالات التجارية، وعقود تقديم الخدمات بين المنشآت المختلفة.",
      icon: <Handshake className="w-6 h-6 text-indigo-950" />
    },
    {
      title: "عقود التطوير والاستثمار العقاري",
      desc: "صياغة اتفاقيات البيع، الشراء، الإيجار طويل الأجل، والمشاريع العقارية لتقليل مخاطر الاستثمار البناء.",
      icon: <FileText className="w-6 h-6 text-indigo-950" />
    }
  ];

  return (
    <div className="bg-white text-right font-sans min-h-screen" dir="rtl">
      <ServicePageHeader 
        title="صياغة وإعداد ومراجعة العقود"
        description="نقدم صياغة قانونية محكمة تحمي استثماراتك وتدعم نمو أعمالك في السوق السعودي"
      />
      
      {/* Hero Header Section - Converted to Premium Light Mode */}
      <section className="relative bg-slate-50 border-b border-slate-100 py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1450133064473-71024230f91b?auto=format&fit=crop&q=80')] bg-cover bg-center"></div>
        {/* White/Slate overlay for modern executive contrast */}
        <div className="absolute inset-0 bg-white/90 backdrop-blur-[1px]"></div>
        
        <div className="max-w-6xl mx-auto relative z-10 text-center">
          <span className="text-blue-700 font-bold text-xs md:text-sm tracking-wide block mb-3 uppercase">حماية أعمالك تبدأ من هنا</span>
          <h1 className="text-3xl md:text-5xl font-extrabold mb-6 text-indigo-950 leading-tight">صياغة وإعداد ومراجعة العقود</h1>
          <p className="text-slate-600 max-w-3xl mx-auto text-base md:text-lg leading-relaxed font-light">
            العقد هو الشريعة التي تحكم علاقاتك التجارية والاستثمارية. نحن نؤمن بأن الصياغة القانونية الرصينة والمحكمة هي خط الدفاع الأول الذي يحميك من النزاعات المستقبلية ويوفر الاستقرار لأعمالك.
          </p>
        </div>
      </section>

      {/* Main Framework (Why it Matters) */}
      <section className="max-w-6xl mx-auto py-20 px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl md:text-4xl font-extrabold text-indigo-950 mb-6 leading-snug">
              لماذا تحتاج إلى صياغة ومراجعة مهنية لعقودك؟
            </h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-4 font-light">
              الكثير من النزاعات القضائية المعقدة تنشأ بسبب ثغرة بسيطة أو عبارة غامضة في العقد تم صياغتها دون دراية قانونية وافية. الصياغة التقليدية أو العشوائية قد تكلف منشأتك الكثير من الوقت والمال.
            </p>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed font-light">
              في مكتبنا، لا نكتفي بملء نماذج جاهزة، بل نقوم بتحليل بيئة عمل العميل، فهم أهدافه التجارية، وتوقع المخاطر المحتملة لوضع شروط صارمة تضمن مصلحته وتوفر له الأمان القانوني التام.
            </p>
          </div>
          
          {/* Methodology Checklist */}
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200/60 space-y-6">
            <div className="flex gap-4 items-start">
              <div className="p-3 bg-white rounded-xl text-indigo-950 border border-slate-100 shadow-sm shrink-0">
                <SearchCheck className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-indigo-950 mb-1">التحليل الدقيق وفحص الثغرات</h3>
                <p className="text-slate-500 text-sm font-light">ندرس الالتزامات المتبادلة ونكشف أي شروط إذعان أو مخاطر خفية قد تضر بمركزك القانوني.</p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="p-3 bg-white rounded-xl text-indigo-950 border border-slate-100 shadow-sm shrink-0">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-indigo-950 mb-1">الامتثال للأنظمة السعودية</h3>
                <p className="text-slate-500 text-sm font-light">نضمن توافق البنود بالكامل مع الأنظمة السارية في المملكة (كنظام المعاملات المدنية، نظام الشركات، ونظام العمل).</p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="p-3 bg-white rounded-xl text-indigo-950 border border-slate-100 shadow-sm shrink-0">
                <FileSignature className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-indigo-950 mb-1">وضوح الالتزامات والإنهاء</h3>
                <p className="text-slate-500 text-sm font-light">نصيغ آليات واضحة لفسخ العقد، التعويضات، الغرامات التوجيهية، وطرق حل النزاعات السلمية أو القضائية.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grid: What We Draft - Converted completely to Light Grid with Top Border accent */}
      <section className="bg-slate-50/50 border-y border-slate-100 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-4xl font-extrabold text-indigo-950 mb-3">أنواع العقود والاتفاقيات التي نتميز بها</h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-sm md:text-base font-light">نقدم حلولاً متكاملة تغطي كافة تطلعات واحتياجات الشركات والمستثمرين والأفراد</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {contractTypes.map((contract, index) => (
              <div key={index} className="bg-white p-8 rounded-xl border-t-4 border-t-indigo-950 border-x border-b border-slate-100 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="bg-slate-50 p-3 rounded-lg w-fit mb-5">
                  {contract.icon}
                </div>
                <h3 className="text-xl font-bold text-indigo-950 mb-2">{contract.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed font-light">{contract.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Procedural Process Sequence */}
      <section className="max-w-6xl mx-auto py-20 px-4">
        <div className="text-center mb-16">
          <h2 className="text-2xl md:text-4xl font-extrabold text-indigo-950 mb-3">منهجية العمل لدينا</h2>
          <p className="text-slate-500 text-sm md:text-base font-light">خطوات مدروسة نتبعها لضمان خروج العقد بأعلى درجات الإحكام والسلامة القانونية</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm relative hover:shadow-md transition-shadow duration-200">
            <span className="absolute -top-4 right-6 bg-indigo-950 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-sm">1</span>
            <h3 className="text-lg font-bold text-indigo-950 mt-2 mb-2">جلسة فهم المتطلبات</h3>
            <p className="text-slate-500 text-sm leading-relaxed font-light">نجلس معك للاستماع بعناية إلى غرض الاتفاق والشروط التجارية المتفق عليها بين الأطراف لترجمتها بدقة.</p>
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm relative hover:shadow-md transition-shadow duration-200">
            <span className="absolute -top-4 right-6 bg-indigo-950 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-sm">2</span>
            <h3 className="text-lg font-bold text-indigo-950 mt-2 mb-2">الصياغة والتقييم الحمائي</h3>
            <p className="text-slate-500 text-sm leading-relaxed font-light">يقوم مستشارونا بصياغة المسودة الأولى مع وضع بنود وقائية تحميك من أي تغيرات مفاجئة في بنود الشراكة أو التوريد.</p>
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm relative hover:shadow-md transition-shadow duration-200">
            <span className="absolute -top-4 right-6 bg-indigo-950 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-sm">3</span>
            <h3 className="text-lg font-bold text-indigo-950 mt-2 mb-2">المراجعة والاعتماد النهائي</h3>
            <p className="text-slate-500 text-sm leading-relaxed font-light">نناقش المسودة معك، ونجري التعديلات اللازمة حتى نصل إلى نسخة نهائية محكمة وجاهزة للتوقيع والتوثيق القانوني.</p>
          </div>
        </div>

        {/* Bottom Alert Tag - Adjusted to Slate Tone */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-full text-slate-700 text-sm border border-slate-200/60 shadow-sm">
            <ShieldAlert className="w-4 h-4 text-indigo-950 shrink-0" />
            <span className="font-medium">لا تخاطر بتوقيع مستند أو اتفاقية دون مراجعتها قانونياً أولاً.</span>
          </div>
        </div>
      </section>

    </div>
  );
}